/**
 * ScheduleWorkoutModal - Modal for scheduling and planning workouts
 *
 * Features:
 * - Schedule date, time, duration
 * - Add planned exercises with target sets
 * - Coach can select client
 * - Start live workout session
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Clock, Calendar, Trash2, AlertCircle, Dumbbell, User, Ruler, Scale,
  Plus, Play, ChevronDown, ChevronUp, GripVertical, Loader2, Pencil, Save
} from 'lucide-react';
import { useFitnessStore } from '../../stores/fitnessStore';
import { useCoachStore } from '../../stores/coachStore';
import { format, parseISO, isBefore, startOfDay } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { v4 as uuidv4 } from 'uuid';
import { getExercisesByCategory } from '../../data/exercises';
import { fitness, supabase } from '../../services/supabaseService';
import toast from 'react-hot-toast';

// Exercise types with Turkish labels
const EXERCISE_TYPES_TR = {
  fitness: { label: 'Fitness', emoji: '🏋️', color: 'blue' },
  kickbox: { label: 'Kickbox', emoji: '🥊', color: 'red' },
  cardio: { label: 'Kardiyo', emoji: '🏃', color: 'green' },
};

// Status labels
const STATUS_LABELS = {
  scheduled: { label: 'Planlandı', color: 'text-blue-400', bg: 'bg-blue-500/20' },
  in_progress: { label: 'Devam Ediyor', color: 'text-yellow-400', bg: 'bg-yellow-500/20' },
  completed: { label: 'Tamamlandı', color: 'text-green-400', bg: 'bg-green-500/20' },
  missed: { label: 'Kaçırıldı', color: 'text-red-400', bg: 'bg-red-500/20' },
  cancelled: { label: 'İptal Edildi', color: 'text-gray-400', bg: 'bg-gray-500/20' },
};

// Planned exercise item component
const PlannedExerciseItem = ({ exercise, index, onUpdate, onRemove, onUpdateSet, exerciseNames, exerciseType = 'fitness', isReadOnly = false, isEditMode = false, comparisonData = null, onSelectExercise }) => {
  const [isExpanded, setIsExpanded] = useState(isReadOnly || isEditMode); // Auto-expand for completed workouts
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const inputRef = useRef(null);

  // Update dropdown position when showing
  useEffect(() => {
    if (showDropdown && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        maxHeight: '200px',
        zIndex: 99999,
      });
    }
  }, [showDropdown]);

  // For completed workouts, show sets data
  const hasSetsData = exercise.sets?.length > 0 && exercise.sets.some(s => s.target_reps || s.actual_reps);

  // Can edit if in edit mode
  const canEditSets = isEditMode && hasSetsData;

  const filteredSuggestions = useMemo(() => {
    if (!searchQuery || searchQuery.length < 1) return [];
    const query = searchQuery.toLowerCase();

    // Get exercises from the library based on exercise type
    const libraryExercises = getExercisesByCategory(exerciseType);

    // Merge with user's past exercises (exerciseNames from store)
    const allExercises = [...new Set([...libraryExercises, ...exerciseNames])];

    // Filter and sort by relevance
    return allExercises
      .filter(name => name.toLowerCase().includes(query))
      .sort((a, b) => {
        // Prioritize exercises that START with the query
        const aStarts = a.toLowerCase().startsWith(query);
        const bStarts = b.toLowerCase().startsWith(query);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.localeCompare(b, 'tr');
      })
      .slice(0, 8);
  }, [searchQuery, exerciseNames, exerciseType]);

  // Show/hide dropdown based on suggestions
  useEffect(() => {
    setShowDropdown(filteredSuggestions.length > 0 && searchQuery.length > 0);
  }, [filteredSuggestions, searchQuery]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="bg-gray-800/50 rounded-xl"
    >
      {/* Header - Two rows */}
      <div className="p-3 space-y-2">
        {/* Row 1: Number, History badges, Set count, Actions */}
        <div className="flex items-center gap-2">
          <div className="text-gray-500 cursor-grab">
            <GripVertical className="w-4 h-4" />
          </div>
          <div className="w-7 h-7 rounded-lg bg-orange-500/20 flex items-center justify-center">
            <span className="text-sm font-bold text-orange-400">{index + 1}</span>
          </div>

          {/* History badges - both always visible, selected one highlighted */}
          {exercise.history && !isReadOnly && (
            <div className="flex items-center gap-1.5 flex-1">
              <button
                type="button"
                onClick={() => {
                  onUpdate(index, {
                    ...exercise,
                    target_weight: exercise.history.last?.weight,
                    target_reps: exercise.history.last?.reps,
                    history_source: 'last',
                  });
                }}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                  exercise.history_source === 'last'
                    ? 'bg-blue-500/40 text-blue-300 ring-1 ring-blue-400'
                    : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                }`}
              >
                En Son {exercise.history.last?.weight}kg
              </button>
              {exercise.history.max?.weight > exercise.history.last?.weight && (
                <button
                  type="button"
                  onClick={() => {
                    onUpdate(index, {
                      ...exercise,
                      target_weight: exercise.history.max?.weight,
                      target_reps: exercise.history.max?.reps,
                      history_source: 'max',
                    });
                  }}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    exercise.history_source === 'max'
                      ? 'bg-yellow-500/40 text-yellow-300 ring-1 ring-yellow-400'
                      : 'bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400'
                  }`}
                >
                  🏆 Max {exercise.history.max?.weight}kg
                </button>
              )}
            </div>
          )}

          {!exercise.history && <div className="flex-1" />}

          <div className="flex items-center gap-1">
            <input
              type="number"
              inputMode="numeric"
              value={exercise.target_sets || ''}
              onChange={(e) => !isReadOnly && onUpdate(index, { ...exercise, target_sets: parseInt(e.target.value) || 0 })}
              placeholder="3"
              min="1"
              max="20"
              readOnly={isReadOnly}
              className={`w-12 px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm text-center focus:outline-none focus:border-orange-500 ${isReadOnly ? 'cursor-default' : ''}`}
            />
            <span className="text-xs text-gray-500">set</span>
          </div>

          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 text-gray-500 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {!isReadOnly && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Row 2: Exercise name input */}
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={exercise.exercise_name}
            onChange={(e) => {
              if (isReadOnly) return;
              onUpdate(index, { ...exercise, exercise_name: e.target.value });
              setSearchQuery(e.target.value);
            }}
            onFocus={() => {
              if (isReadOnly) return;
              setSearchQuery(exercise.exercise_name);
            }}
            onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
            placeholder="Egzersiz adı yazın..."
            readOnly={isReadOnly}
            className={`w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500 ${isReadOnly ? 'cursor-default' : ''}`}
          />

          {/* Autocomplete dropdown - portal to body */}
          {showDropdown && filteredSuggestions.length > 0 && createPortal(
            <div
              className="bg-gray-800 border border-gray-600 rounded-lg shadow-2xl overflow-y-auto"
              style={dropdownStyle}
            >
              {filteredSuggestions.map((name, i) => (
                <button
                  key={i}
                  type="button"
                  onMouseDown={async (e) => {
                    e.preventDefault();
                    setSearchQuery('');
                    setShowDropdown(false);
                    if (onSelectExercise) {
                      await onSelectExercise(index, name);
                    } else {
                      onUpdate(index, { ...exercise, exercise_name: name });
                    }
                  }}
                  className="w-full px-3 py-2.5 text-left text-sm text-gray-300 hover:bg-gray-700 hover:text-white border-b border-gray-700/50 last:border-0"
                >
                  {name}
                </button>
              ))}
            </div>,
            document.body
          )}
        </div>
      </div>

      {/* Expanded details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-1 border-t border-gray-700/50">
              {/* Comparison header if data exists */}
              {comparisonData && (
                <div className="mb-2 px-2 py-1.5 bg-blue-500/10 rounded-lg flex items-center gap-2">
                  <span className="text-xs text-blue-400">
                    Önceki: {format(parseISO(comparisonData.date), 'd MMM', { locale: tr })}
                  </span>
                </div>
              )}

              {/* Show individual sets for completed workouts */}
              {hasSetsData ? (
                <div className="space-y-2">
                  <div className="grid grid-cols-4 gap-2 text-xs text-gray-500 px-2">
                    <span>Set</span>
                    <span>Tekrar</span>
                    <span>Ağırlık</span>
                    <span>Not</span>
                  </div>
                  {exercise.sets.map((set, setIdx) => {
                    // Get previous set data for comparison
                    const prevSet = comparisonData?.sets?.[setIdx];
                    const currentWeight = parseFloat(set.target_weight || set.actual_weight) || 0;
                    const prevWeight = prevSet?.weight || 0;
                    const weightDiff = currentWeight - prevWeight;

                    return (
                      <div key={setIdx} className="space-y-1">
                        <div className="grid grid-cols-4 gap-2 items-center bg-gray-700/30 rounded-lg px-2 py-2">
                          <span className="text-sm font-medium text-orange-400">{setIdx + 1}</span>
                          {canEditSets ? (
                            <>
                              <input
                                type="number"
                                inputMode="numeric"
                                value={set.target_reps || ''}
                                onChange={(e) => onUpdateSet(index, setIdx, 'target_reps', e.target.value)}
                                className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm text-center focus:outline-none focus:border-orange-500"
                              />
                              <input
                                type="number"
                                inputMode="decimal"
                                step="0.5"
                                value={set.target_weight || ''}
                                onChange={(e) => onUpdateSet(index, setIdx, 'target_weight', e.target.value)}
                                className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm text-center focus:outline-none focus:border-orange-500"
                              />
                              <input
                                type="text"
                                value={set.notes || ''}
                                onChange={(e) => onUpdateSet(index, setIdx, 'notes', e.target.value)}
                                placeholder="-"
                                className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs focus:outline-none focus:border-orange-500"
                              />
                            </>
                          ) : (
                            <>
                              <span className="text-sm text-white">{set.target_reps || set.actual_reps || '-'}</span>
                              <div className="flex items-center gap-1">
                                <span className="text-sm text-orange-400 font-medium">{set.target_weight || set.actual_weight || '-'} kg</span>
                                {prevSet && weightDiff !== 0 && (
                                  <span className={`text-xs ${weightDiff > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {weightDiff > 0 ? '+' : ''}{weightDiff.toFixed(1)}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-400 truncate">{set.notes || '-'}</span>
                            </>
                          )}
                        </div>
                        {/* Show previous set info below */}
                        {prevSet && !canEditSets && (
                          <div className="grid grid-cols-4 gap-2 px-2 text-xs text-gray-500">
                            <span></span>
                            <span>({prevSet.reps})</span>
                            <span>({prevSet.weight} kg)</span>
                            <span></span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                /* Planning mode - editable inputs */
                <>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                        Tekrar
                        {exercise.history_source && (
                          <span className={`text-[10px] ${exercise.history_source === 'max' ? 'text-yellow-400' : 'text-blue-400'}`}>
                            ({exercise.history_source === 'max' ? 'Max' : 'En Son'})
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={exercise.target_reps || ''}
                        onChange={(e) => onUpdate(index, { ...exercise, target_reps: parseInt(e.target.value) || null })}
                        placeholder="12"
                        disabled={isReadOnly}
                        className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500 disabled:opacity-50"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                        Ağırlık (kg)
                        {exercise.history_source && (
                          <span className={`text-[10px] ${exercise.history_source === 'max' ? 'text-yellow-400' : 'text-blue-400'}`}>
                            ({exercise.history_source === 'max' ? 'Max' : 'En Son'})
                          </span>
                        )}
                      </label>
                      <input
                        type="number"
                        inputMode="decimal"
                        step="0.5"
                        value={exercise.target_weight || ''}
                        onChange={(e) => onUpdate(index, { ...exercise, target_weight: parseFloat(e.target.value) || null })}
                        placeholder="50"
                        disabled={isReadOnly}
                        className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500 disabled:opacity-50"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">Not</label>
                    <input
                      type="text"
                      value={exercise.notes || ''}
                      onChange={(e) => onUpdate(index, { ...exercise, notes: e.target.value })}
                      placeholder="Isınma dahil, son set drop set..."
                      disabled={isReadOnly}
                      className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-orange-500 disabled:opacity-50"
                    />
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const ScheduleWorkoutModal = ({ isOpen, onClose }) => {
  const {
    selectedScheduledWorkout: selectedWorkoutFromStore,
    scheduledWorkouts,
    scheduleModalDate,
    scheduleModalTime,
    addScheduledWorkout,
    updateScheduledWorkout,
    deleteScheduledWorkout,
    markScheduledWorkoutMissed,
    closeScheduleWorkoutModal,
    openLiveWorkoutModal,
    exerciseNames,
    loadExerciseNames,
    getExerciseHistory,
  } = useFitnessStore();

  const { isActingAsCoach, clients, viewingClientId } = useCoachStore();

  // Get the latest version of the workout from the list (for realtime updates)
  const selectedScheduledWorkout = useMemo(() => {
    if (!selectedWorkoutFromStore?.id) return selectedWorkoutFromStore;
    return scheduledWorkouts.find(w => w.id === selectedWorkoutFromStore.id) || selectedWorkoutFromStore;
  }, [selectedWorkoutFromStore, scheduledWorkouts]);

  const isEditing = !!selectedScheduledWorkout;

  // Form state
  const [date, setDate] = useState(scheduleModalDate || format(new Date(), 'yyyy-MM-dd'));
  const [time, setTime] = useState(scheduleModalTime || '10:00');
  const [exerciseType, setExerciseType] = useState('fitness');
  const [durationMinutes, setDurationMinutes] = useState('60');
  const [notes, setNotes] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [plannedExercises, setPlannedExercises] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'exercises'
  const [isLoadingCompletedExercises, setIsLoadingCompletedExercises] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSavingEdits, setIsSavingEdits] = useState(false);
  const [comparisonData, setComparisonData] = useState({}); // { exerciseName: { date, sets: [{weight, reps}] } }

  // Get selected client info
  const selectedClient = useMemo(() => {
    if (!selectedClientId || !clients.length) return null;
    const clientRelation = clients.find(c => c.client_id === selectedClientId);
    return clientRelation?.client || null;
  }, [selectedClientId, clients]);

  // Body scroll lock
  useBodyScrollLock(isOpen);

  // Load exercise names for autocomplete
  useEffect(() => {
    if (isOpen && (!exerciseNames || exerciseNames.length === 0)) {
      loadExerciseNames();
    }
  }, [isOpen, exerciseNames, loadExerciseNames]);

  // Handle close
  const handleClose = useCallback(() => {
    closeScheduleWorkoutModal();
    if (onClose) onClose();
  }, [closeScheduleWorkoutModal, onClose]);

  // ESC key
  useEffect(() => {
    if (!isOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen, handleClose]);

  // Convert fitness_workouts rows to planned_exercises format
  // For completed workouts, we show actual values in target fields for display
  // Also handles legacy reps_detail format
  const convertWorkoutsToPlannedExercises = useCallback((workouts) => {
    if (!workouts?.length) return [];

    // First, expand legacy records that have reps_detail into individual sets
    const expandedWorkouts = [];
    workouts.forEach(w => {
      // Check if this is a legacy record with reps_detail (multiple sets in one record)
      if (w.reps_detail && Array.isArray(w.reps_detail) && w.reps_detail.length > 0) {
        // Expand each set in reps_detail into a virtual workout record
        w.reps_detail.forEach((setDetail, idx) => {
          expandedWorkouts.push({
            ...w,
            id: `${w.id}_set_${idx}`, // Virtual ID for UI
            _originalId: w.id, // Keep original for editing
            _setIndex: idx,
            reps: setDetail.reps,
            weight: setDetail.weight,
            notes: setDetail.notes || w.notes,
          });
        });
      } else {
        // New format: single set per row
        expandedWorkouts.push(w);
      }
    });

    // Group by exercise_name
    const exerciseMap = new Map();
    expandedWorkouts.forEach(w => {
      if (!exerciseMap.has(w.exercise_name)) {
        exerciseMap.set(w.exercise_name, {
          id: uuidv4(),
          exercise_name: w.exercise_name,
          target_sets: 0,
          notes: '',
          sets: [],
        });
      }
      const exercise = exerciseMap.get(w.exercise_name);
      exercise.sets.push({
        fitness_workout_id: w._originalId || w.id, // Keep original ID for updates
        _setIndex: w._setIndex, // For legacy format editing
        _isLegacy: !!w._originalId, // Flag for legacy format
        set_number: exercise.sets.length + 1,
        // Put actual values in target fields for display in PlannedExerciseItem
        target_reps: w.reps,
        target_weight: w.weight,
        actual_reps: w.reps,
        actual_weight: w.weight,
        duration_seconds: w.duration_seconds || null,
        notes: w.notes || '',
      });
      exercise.target_sets = exercise.sets.length;
    });

    return Array.from(exerciseMap.values());
  }, []);

  // Load comparison data for completed workouts
  const loadComparisonData = useCallback(async (exercises, workoutDate, workoutUserId) => {
    if (!exercises?.length) return;

    const comparison = {};
    const exerciseNameList = exercises.map(e => e.exercise_name).filter(Boolean);

    await Promise.all(
      exerciseNameList.map(async (exerciseName) => {
        try {
          const previousSets = await fitness.getPreviousWorkoutSets(
            exerciseName,
            workoutDate,
            workoutUserId || null
          );

          if (previousSets.length > 0) {
            comparison[exerciseName] = {
              date: previousSets[0].date,
              sets: previousSets.map(s => ({
                weight: parseFloat(s.weight) || 0,
                reps: s.reps || 0,
              })),
            };
          }
        } catch (err) {
          console.error(`Error fetching comparison for ${exerciseName}:`, err);
        }
      })
    );

    setComparisonData(comparison);
  }, []);

  // Populate form when editing
  useEffect(() => {
    if (selectedScheduledWorkout) {
      setDate(selectedScheduledWorkout.scheduled_date);
      setTime(selectedScheduledWorkout.scheduled_time?.substring(0, 5) || '10:00');
      setExerciseType(selectedScheduledWorkout.exercise_type || 'fitness');
      setDurationMinutes(selectedScheduledWorkout.duration_minutes?.toString() || '60');
      setNotes(selectedScheduledWorkout.notes || '');
      setSelectedClientId(selectedScheduledWorkout.user_id || '');
      setComparisonData({}); // Reset comparison data

      // Load planned exercises
      if (selectedScheduledWorkout.planned_exercises?.length) {
        setPlannedExercises(selectedScheduledWorkout.planned_exercises);
        setActiveTab('exercises');
        // Load comparison if completed
        if (selectedScheduledWorkout.status === 'completed') {
          loadComparisonData(
            selectedScheduledWorkout.planned_exercises,
            selectedScheduledWorkout.scheduled_date,
            selectedScheduledWorkout.user_id
          );
        }
      } else if (selectedScheduledWorkout.status === 'completed') {
        // For completed workouts with no planned_exercises, try to load from fitness_workouts
        setIsLoadingCompletedExercises(true);
        fitness.getAllWorkoutsByScheduledId(selectedScheduledWorkout.id)
          .then(async workouts => {
            const exercises = convertWorkoutsToPlannedExercises(workouts);
            if (exercises.length > 0) {
              setPlannedExercises(exercises);
              setActiveTab('exercises');
              // Load comparison data
              await loadComparisonData(
                exercises,
                selectedScheduledWorkout.scheduled_date,
                selectedScheduledWorkout.user_id
              );
            } else {
              setPlannedExercises([]);
              setActiveTab('details');
            }
          })
          .catch(err => {
            console.error('Error loading completed exercises:', err);
            setPlannedExercises([]);
            setActiveTab('details');
          })
          .finally(() => setIsLoadingCompletedExercises(false));
      } else {
        setPlannedExercises([]);
        setActiveTab('details');
      }
    } else {
      setDate(scheduleModalDate || format(new Date(), 'yyyy-MM-dd'));
      setTime(scheduleModalTime || '10:00');
      setExerciseType('fitness');
      setDurationMinutes('60');
      setNotes('');
      // Default to viewing client if coach is viewing a specific client's calendar
      setSelectedClientId(viewingClientId || '');
      setPlannedExercises([]);
      setComparisonData({});
      setActiveTab('details');
    }
  }, [selectedScheduledWorkout, scheduleModalDate, scheduleModalTime, viewingClientId, convertWorkoutsToPlannedExercises, loadComparisonData]);

  // Add exercise handler
  const handleAddExercise = () => {
    const newExercise = {
      id: uuidv4(),
      exercise_name: '',
      target_sets: 3,
      target_reps: null,
      target_weight: null,
      notes: '',
      sets: [], // Will be populated when starting workout
    };
    setPlannedExercises([...plannedExercises, newExercise]);
    setActiveTab('exercises');
  };

  // Update exercise handler
  const handleUpdateExercise = (index, updatedExercise) => {
    const newExercises = [...plannedExercises];
    newExercises[index] = updatedExercise;
    setPlannedExercises(newExercises);
  };

  // Select exercise and fetch history (last + max)
  const handleSelectExercise = async (index, exerciseName) => {
    console.log('[handleSelectExercise] Called for:', exerciseName);
    const newExercises = [...plannedExercises];
    const currentExercise = newExercises[index];

    // Get exercise history for the person this workout is being scheduled for
    // If scheduling for a client, get client's history; otherwise get current user's history
    const targetUserId = selectedClientId || viewingClientId || null;
    const history = await getExerciseHistory(exerciseName, targetUserId);
    console.log('[handleSelectExercise] history:', history);

    newExercises[index] = {
      ...currentExercise,
      exercise_name: exerciseName,
      // Store history for display (last/max toggle)
      history: history || null,
      // Auto-fill with last values
      ...(history?.last?.weight && { target_weight: history.last.weight }),
      ...(history?.last?.reps && { target_reps: history.last.reps }),
    };

    console.log('[handleSelectExercise] Updated exercise:', newExercises[index]);
    setPlannedExercises(newExercises);
  };

  // Remove exercise handler
  const handleRemoveExercise = (index) => {
    setPlannedExercises(plannedExercises.filter((_, i) => i !== index));
  };

  // Update a specific set within an exercise (for edit mode)
  const handleUpdateSet = (exerciseIndex, setIndex, field, value) => {
    setPlannedExercises(prev => {
      const newExercises = [...prev];
      const exercise = { ...newExercises[exerciseIndex] };
      const sets = [...exercise.sets];
      sets[setIndex] = {
        ...sets[setIndex],
        [field]: value,
        // Keep target and actual in sync for display
        ...(field === 'target_reps' ? { actual_reps: value } : {}),
        ...(field === 'target_weight' ? { actual_weight: value } : {}),
      };
      exercise.sets = sets;
      newExercises[exerciseIndex] = exercise;
      return newExercises;
    });
  };

  // Save edits to completed workout
  const handleSaveCompletedEdits = async () => {
    setIsSavingEdits(true);
    try {
      // Group sets by fitness_workout_id for legacy format handling
      const legacyUpdates = new Map(); // id -> reps_detail array
      const directUpdates = []; // Regular updates

      plannedExercises.forEach(exercise => {
        exercise.sets.forEach(set => {
          if (!set.fitness_workout_id) return;

          if (set._isLegacy && set._setIndex !== undefined) {
            // Legacy format: collect sets to rebuild reps_detail array
            if (!legacyUpdates.has(set.fitness_workout_id)) {
              legacyUpdates.set(set.fitness_workout_id, []);
            }
            const setsArray = legacyUpdates.get(set.fitness_workout_id);
            setsArray[set._setIndex] = {
              reps: parseInt(set.target_reps) || 0,
              weight: parseFloat(set.target_weight) || 0,
              notes: set.notes || null,
            };
          } else {
            // New format: direct update
            directUpdates.push({
              id: set.fitness_workout_id,
              reps: parseInt(set.target_reps) || 0,
              weight: parseFloat(set.target_weight) || 0,
              notes: set.notes || null,
            });
          }
        });
      });

      // Update legacy records (reps_detail array)
      for (const [id, repsDetail] of legacyUpdates) {
        const { error } = await supabase
          .from('fitness_workouts')
          .update({ reps_detail: repsDetail })
          .eq('id', id);
        if (error) throw error;
      }

      // Update new format records
      for (const update of directUpdates) {
        const { error } = await supabase
          .from('fitness_workouts')
          .update({ reps: update.reps, weight: update.weight, notes: update.notes })
          .eq('id', update.id);
        if (error) throw error;
      }

      setIsEditMode(false);
      toast.success('Değişiklikler kaydedildi');
    } catch (error) {
      console.error('Error saving edits:', error);
      toast.error('Kaydetme başarısız');
    } finally {
      setIsSavingEdits(false);
    }
  };

  // Prepare exercises with sets for live workout
  // Pre-fill actual values with target values if history was selected
  const prepareExercisesForLiveWorkout = (exercises) => {
    return exercises.map(ex => ({
      ...ex,
      sets: Array.from({ length: ex.target_sets || 3 }, (_, i) => ({
        set_number: i + 1,
        target_reps: ex.target_reps,
        target_weight: ex.target_weight,
        // Pre-fill actual values if target exists (from history selection)
        actual_reps: ex.target_reps || null,
        actual_weight: ex.target_weight || null,
        coach_reps: null,
        coach_weight: null,
        started_at: null,
        completed_at: null,
        duration_seconds: null,
      })),
    }));
  };

  // Submit handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);

    // Prepare exercises with sets
    const preparedExercises = prepareExercisesForLiveWorkout(
      plannedExercises.filter(ex => ex.exercise_name.trim())
    );

    const data = {
      scheduled_date: date,
      scheduled_time: time + ':00',
      duration_minutes: parseInt(durationMinutes) || 60,
      exercise_type: exerciseType,
      notes: notes.trim() || null,
      planned_exercises: preparedExercises,
      ...(isActingAsCoach && selectedClientId && { user_id: selectedClientId }),
    };

    try {
      if (isEditing) {
        await updateScheduledWorkout(selectedScheduledWorkout.id, data);
        // Modal stays open after update - user can close manually
      } else {
        await addScheduledWorkout(data);
        handleClose();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (selectedScheduledWorkout && confirm('Bu planı silmek istediğinize emin misiniz?')) {
      await deleteScheduledWorkout(selectedScheduledWorkout.id);
      handleClose();
    }
  };

  // Start live workout - just opens the modal, actual start happens inside LiveWorkoutModal
  const handleStartLiveWorkout = async () => {
    if (!selectedScheduledWorkout) return;

    // If workout is already in_progress, use the DB data (contains other user's inputs)
    if (selectedScheduledWorkout.status === 'in_progress') {
      handleClose();
      openLiveWorkoutModal(selectedScheduledWorkout);
      return;
    }

    // Prepare exercises for new workout
    const preparedExercises = prepareExercisesForLiveWorkout(
      plannedExercises.filter(ex => ex.exercise_name.trim())
    );

    // Save exercises first (but don't start the workout yet - let user choose companion mode)
    if (plannedExercises.length > 0) {
      await updateScheduledWorkout(selectedScheduledWorkout.id, {
        planned_exercises: preparedExercises,
      });
    }

    // Open modal without starting - user will click "Antrenmanı Başlat" inside
    const workoutData = {
      ...selectedScheduledWorkout,
      planned_exercises: preparedExercises,
    };

    handleClose();
    openLiveWorkoutModal(workoutData);
  };

  // Mark missed
  const handleMarkMissed = async () => {
    if (selectedScheduledWorkout && confirm('Bu planı kaçırıldı olarak işaretlemek istediğinize emin misiniz?')) {
      await markScheduledWorkoutMissed(selectedScheduledWorkout.id);
      handleClose();
    }
  };

  const isPastSchedule = isEditing && isBefore(
    parseISO(selectedScheduledWorkout.scheduled_date),
    startOfDay(new Date())
  );

  const status = selectedScheduledWorkout?.status || 'scheduled';
  const statusInfo = STATUS_LABELS[status];
  const canStartWorkout = isEditing && (status === 'scheduled' || status === 'in_progress');

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
        onClick={handleClose}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

        <motion.div
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full sm:max-w-lg bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Mobile drag indicator */}
          <div className="sm:hidden flex justify-center pt-3 pb-1">
            <div className="w-10 h-1 bg-gray-700 rounded-full" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 sm:p-4 border-b border-gray-800">
            <div>
              <h2 className="text-base sm:text-lg font-semibold text-white">
                {isEditing ? 'Antrenman Planı' : 'Antrenman Planla'}
              </h2>
              {isEditing && (
                <div className={`inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full text-xs ${statusInfo.bg} ${statusInfo.color}`}>
                  {statusInfo.label}
                </div>
              )}
            </div>
            <button
              onClick={handleClose}
              className="p-2.5 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-800">
            <button
              onClick={() => setActiveTab('details')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'details'
                  ? 'text-orange-400 border-b-2 border-orange-400'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              Detaylar
            </button>
            <button
              onClick={() => setActiveTab('exercises')}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === 'exercises'
                  ? 'text-orange-400 border-b-2 border-orange-400'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              Egzersizler ({plannedExercises.length})
            </button>
          </div>

          {/* Content */}
          <div className={`flex-1 p-4 ${activeTab === 'details' ? 'overflow-y-auto' : 'overflow-visible'}`}>
            {activeTab === 'details' ? (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Client Selector */}
                {isActingAsCoach && clients.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1.5">
                      <User className="w-4 h-4 inline mr-1" />
                      Öğrenci
                    </label>
                    <select
                      value={selectedClientId}
                      onChange={(e) => setSelectedClientId(e.target.value)}
                      className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Kendim için</option>
                      {clients.map((client) => (
                        <option key={client.client_id} value={client.client_id}>
                          {client.client?.display_name || client.client?.email || 'Bilinmiyor'}
                        </option>
                      ))}
                    </select>

                    {/* Client info card */}
                    {selectedClient && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-3 p-3 bg-violet-500/10 border border-violet-500/30 rounded-xl"
                      >
                        <div className="flex items-center gap-3">
                          {selectedClient.avatar_url ? (
                            <img
                              src={selectedClient.avatar_url}
                              alt={selectedClient.display_name}
                              className="w-12 h-12 rounded-xl object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 rounded-xl bg-violet-500/30 flex items-center justify-center">
                              <User className="w-6 h-6 text-violet-400" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-semibold text-white">
                              {selectedClient.display_name || selectedClient.email}
                            </h4>
                            <div className="flex items-center gap-3 mt-0.5 text-sm text-gray-400">
                              {selectedClient.height_cm && (
                                <span><Ruler className="w-3 h-3 inline mr-1" />{selectedClient.height_cm} cm</span>
                              )}
                              {selectedClient.weight_kg && (
                                <span><Scale className="w-3 h-3 inline mr-1" />{selectedClient.weight_kg} kg</span>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </div>
                )}

                {/* Date, Time, Duration - single row with flex */}
                <div className="flex gap-2 sm:gap-3">
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1">
                      <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                      Tarih
                    </label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full px-2 py-2 sm:py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="w-20 sm:flex-1 shrink-0">
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1">
                      <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1 hidden sm:inline" />
                      Saat
                    </label>
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="w-full px-1.5 sm:px-2 py-2 sm:py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:ring-2 focus:ring-blue-500"
                      required
                    />
                  </div>
                  <div className="w-16 sm:flex-1 shrink-0">
                    <label className="block text-xs sm:text-sm font-medium text-gray-400 mb-1">Süre</label>
                    <div className="relative">
                      <input
                        type="number"
                        inputMode="numeric"
                        value={durationMinutes}
                        onChange={(e) => setDurationMinutes(e.target.value)}
                        min="5"
                        max="300"
                        className="w-full px-1 sm:px-2 py-2 sm:py-2.5 pr-6 sm:pr-8 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm text-center focus:ring-2 focus:ring-blue-500"
                      />
                      <span className="absolute right-1.5 sm:right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">dk</span>
                    </div>
                  </div>
                </div>

                {/* Exercise Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">Antrenman Tipi</label>
                  <div className="grid grid-cols-3 gap-2">
                    {Object.entries(EXERCISE_TYPES_TR).map(([type, config]) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setExerciseType(type)}
                        className={`px-3 py-3 rounded-lg text-sm font-medium transition-all ${
                          exerciseType === type
                            ? type === 'fitness' ? 'bg-blue-600 text-white ring-2 ring-blue-400'
                              : type === 'kickbox' ? 'bg-red-600 text-white ring-2 ring-red-400'
                              : 'bg-green-600 text-white ring-2 ring-green-400'
                            : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                        }`}
                      >
                        <span className="mr-1">{config.emoji}</span>
                        {config.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1.5">Notlar</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Hedefler, hatırlatmalar..."
                    rows={2}
                    className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                </div>

                {/* Past schedule warning */}
                {isPastSchedule && status === 'scheduled' && (
                  <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0" />
                    <p className="text-sm text-yellow-400">
                      Bu plan geçmiş bir tarih için.
                    </p>
                  </div>
                )}
              </form>
            ) : (
              /* Exercises Tab */
              <div className="space-y-3 max-h-[50vh] overflow-y-auto overflow-x-visible pb-4">
                {isLoadingCompletedExercises ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 text-orange-400 animate-spin" />
                    <span className="ml-2 text-gray-400">Egzersizler yükleniyor...</span>
                  </div>
                ) : (
                <>
                <AnimatePresence>
                  {plannedExercises.map((exercise, index) => (
                    <PlannedExerciseItem
                      key={exercise.id}
                      exercise={exercise}
                      index={index}
                      onUpdate={handleUpdateExercise}
                      onRemove={handleRemoveExercise}
                      onUpdateSet={handleUpdateSet}
                      onSelectExercise={handleSelectExercise}
                      exerciseNames={exerciseNames}
                      exerciseType={exerciseType}
                      isReadOnly={status === 'completed' && !isEditMode}
                      isEditMode={isEditMode}
                      comparisonData={comparisonData[exercise.exercise_name] || null}
                    />
                  ))}
                </AnimatePresence>

                {/* Add exercise button */}
                {status !== 'completed' && (
                  <button
                    type="button"
                    onClick={handleAddExercise}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-700 rounded-xl text-gray-400 hover:border-orange-500 hover:text-orange-400 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Egzersiz Ekle</span>
                  </button>
                )}

                {plannedExercises.length === 0 && !isLoadingCompletedExercises && (
                  <div className="text-center py-8">
                    <Dumbbell className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-500 text-sm">
                      {status === 'completed'
                        ? 'Bu antrenman için egzersiz kaydı bulunamadı.'
                        : <>Henüz egzersiz eklenmedi.<br />Yapılacak egzersizleri şimdiden planlayın.</>
                      }
                    </p>
                  </div>
                )}
                </>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-800 space-y-2 safe-area-bottom">
            {/* Start Live Workout Button */}
            {canStartWorkout && (
              <button
                type="button"
                onClick={handleStartLiveWorkout}
                className="w-full flex items-center justify-center gap-2 py-3 bg-green-600 hover:bg-green-500 text-white font-semibold rounded-xl transition-colors"
              >
                <Play className="w-5 h-5" />
                <span>{status === 'in_progress' ? 'Antrenmana Devam Et' : 'Antrenmanı Başlat'}</span>
              </button>
            )}

            {/* Edit mode buttons for completed workouts */}
            {status === 'completed' && plannedExercises.length > 0 && (
              isEditMode ? (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setIsEditMode(false)}
                    className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-lg transition-colors"
                  >
                    Düzenlemeyi İptal Et
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveCompletedEdits}
                    disabled={isSavingEdits}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white font-medium rounded-xl transition-colors"
                  >
                    {isSavingEdits ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Save className="w-5 h-5" />
                    )}
                    <span>{isSavingEdits ? 'Kaydediliyor...' : 'Kaydet'}</span>
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-lg transition-colors"
                  >
                    Kapat
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditMode(true)}
                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-orange-600 hover:bg-orange-500 text-white font-semibold rounded-xl transition-colors"
                  >
                    <Pencil className="w-5 h-5" />
                    <span>Düzenle</span>
                  </button>
                </div>
              )
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              {/* Delete button - always show for editing */}
              {isEditing && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="p-2.5 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                  title="Planı Sil"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              )}

              {isPastSchedule && status === 'scheduled' && (
                <button
                  type="button"
                  onClick={handleMarkMissed}
                  className="px-4 py-3 bg-red-600/20 hover:bg-red-600/30 text-red-400 font-medium rounded-lg transition-colors border border-red-600/30"
                >
                  Kaçırıldı
                </button>
              )}

              <button
                type="button"
                onClick={handleClose}
                className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium rounded-lg transition-colors"
              >
                {isEditing ? 'Kapat' : 'İptal'}
              </button>

              {status !== 'completed' && (
                <button
                  type="submit"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-medium rounded-lg transition-colors"
                >
                  {isSubmitting ? 'Kaydediliyor...' : (isEditing ? 'Güncelle' : 'Planla')}
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default ScheduleWorkoutModal;
